import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { meaningfulItemName } from '../utils/itemDisplay';
import { inputSx } from '../theme';

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

export function getPoItemId(item) {
  return item?.purchase_order_item_id || item?.po_item_id || item?.id || '';
}

function poItemLabel(item) {
  if (!item) return '';
  const name = meaningfulItemName(item?.item_name, item?.item_id) || '—';
  const ordered = item?.ordered_quantity;
  const pending = item?.pending_quantity;
  if (ordered != null && ordered !== '' && pending != null && pending !== '') {
    return `${name} (Ord: ${ordered}, Pending: ${pending})`;
  }
  return ordered != null && ordered !== ''
    ? `${name} (Ord: ${ordered})`
    : name;
}

function filterPoItems(options, query) {
  if (!query || query.length < MIN_SEARCH_LENGTH) return [];
  const q = query.toLowerCase();
  return options.filter(item => poItemLabel(item).toLowerCase().includes(q));
}

/** Debounced searchable picker limited to a PO's line items (client-side filter). */
export default function PoItemAutocomplete({
  value,
  options = [],
  onChange,
  placeholder = 'Search PO item…',
  sx,
}) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(inputValue.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    if (!value) {
      setSelectedItem(null);
      return;
    }
    if (selectedItem && getPoItemId(selectedItem) === value) return;
    const found = options.find(item => getPoItemId(item) === value) || null;
    if (found) setSelectedItem(found);
  }, [value, options, selectedItem]);

  const isSearching = inputValue.trim().length >= MIN_SEARCH_LENGTH;

  const filteredOptions = useMemo(() => {
    const inputQuery = inputValue.trim();
    if (inputQuery.length >= MIN_SEARCH_LENGTH && debouncedSearch.length < MIN_SEARCH_LENGTH) {
      return [];
    }
    return filterPoItems(options, debouncedSearch);
  }, [options, debouncedSearch, inputValue]);

  const displayOptions = useMemo(() => {
    if (isSearching || !selectedItem) return filteredOptions;
    const id = getPoItemId(selectedItem);
    if (filteredOptions.some(item => getPoItemId(item) === id)) return filteredOptions;
    return [selectedItem, ...filteredOptions];
  }, [filteredOptions, selectedItem, isSearching]);

  const noOptionsText = (() => {
    if (inputValue.trim().length < MIN_SEARCH_LENGTH) {
      return `Type at least ${MIN_SEARCH_LENGTH} characters`;
    }
    return 'No matching PO items';
  })();

  return (
    <Autocomplete
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={displayOptions}
      value={selectedItem}
      inputValue={inputValue}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input' || reason === 'clear' || reason === 'reset') {
          setInputValue(newInput);
          if (
            (reason === 'input' || reason === 'clear')
            && selectedItem
            && newInput.trim() !== poItemLabel(selectedItem)
          ) {
            setSelectedItem(null);
            onChange(null);
          }
        }
      }}
      onChange={(_, item) => {
        setSelectedItem(item);
        onChange(item || null);
      }}
      getOptionLabel={poItemLabel}
      isOptionEqualToValue={(a, b) => getPoItemId(a) === getPoItemId(b)}
      filterOptions={(x) => x}
      noOptionsText={noOptionsText}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          sx={{ ...inputSx, ...sx }}
        />
      )}
    />
  );
}
